<template>
  <div>
    <span v-if="showSuccess">
      <HSuccess />
      <h2 class="text-center mw-300 mx-auto mb-10 green--text">
        Pagamento realizado com sucesso!
      </h2>
    </span>

    <v-form v-else ref="form" lazy-validation>
      <h2 class="mb-5">{{ labels.cardInfo }}</h2>

      <v-text-field
        v-model="credit_card.info.number"
        :rules="[rules.number]"
        :label="labels.creditCard"
        v-mask="'#### #### #### ####'"
        type="tel"
        outlined />

      <v-text-field
        v-model="credit_card.info.holder_name"
        :rules="[rules.name]"
        :label="labels.name"
        type="text"
        outlined />

      <v-row>
        <v-col cols="4" sm="3" class="pt-0">
          <v-text-field
            v-model="expire"
            v-mask="'##/####'"
            :error="expiryMonthError"
            :rules="[
              rules.required,
              rules.expiredMonthValidation,
              rules.expiredYearValidation,
            ]"
            :label="labels.cardDate"
            :hint="labels.cardDateHint"
            type="tel"
            outlined />
        </v-col>
        <v-col cols="3" sm="2" class="pt-0">
          <v-text-field
            v-model="credit_card.info.ccv"
            :rules="[rules.ccv]"
            :label="labels.ccv"
            v-mask="'####'"
            type="tel"
            hint="Ex.: 123"
            outlined />
        </v-col>
        <v-col cols="5" sm="7" class="pt-0">
          <v-select
            v-model="installment_amount"
            :items="installmentNumbers"
            :label="labels.installments"
            :rules="[rules.required]"
            outlined />
        </v-col>
      </v-row>

      <h2 class="mb-5">{{ labels.personalInfo }}</h2>

      <v-text-field
        v-model="credit_card.holder.name"
        :rules="[rules.name]"
        :label="labels.name"
        outlined />
      <v-row>
        <v-col cols="12" md="7" class="pt-0 pb-0">
          <v-text-field
            v-model="credit_card.holder.email"
            :rules="[rules.email]"
            :label="labels.email"
            outlined />
        </v-col>
        <v-col cols="12" md="5" class="pt-0 pb-0">
          <v-text-field
            v-model="credit_card.holder.cpf_cnpj"
            v-mask="documentMask"
            :rules="documentRules"
            :label="labels.document"
            outlined />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="4" class="pt-0 pb-0">
          <v-text-field
            v-model="credit_card.holder.phone"
            v-mask="credit_card.holder.phone.length >= 15 ? mask.phone.phone9 : mask.phone.phone8"
            :rules="[rules.phoneValidation]"
            :label="labels.phone"
            outlined />
        </v-col>
        <v-col cols="12" md="8" class="pt-0 pb-0">
          <v-text-field
            v-model="address"
            :label="labels.address"
            :rules="[rules.required]"
            outlined />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6" class="pt-0 pb-0">
          <v-text-field
            v-model="credit_card.holder.address_number"
            :label="labels.addressNumber"
            :rules="[rules.required]"
            outlined />
        </v-col>
        <v-col cols="12" md="6" class="pt-0 pb-0">
          <v-text-field
            v-model="credit_card.holder.postal_code"
            v-mask="'#####-###'"
            :label="labels.postalCode"
            :rules="[rules.required]"
            outlined />
        </v-col>
      </v-row>

      <div class="d-flex justify-end">
        <v-btn @click="submit" :loading="loading" color="primary" elevation="0" large>
          {{ labels.actionLabel }}
        </v-btn>
      </div>
    </v-form>
  </div>
</template>

<script>
// utils
import {
  name,
  email,
  required,
  expiredYearValidation,
  expiredMonthValidation,
  phone as phoneValidation,
} from '@/util/errorHandling'
import { validateCPF, validateCNPJ } from '@/util/validate'
import { cpfCnpj, phone } from '@/util/masks'

// api
import { chargeSubscription } from '../checkoutService'

export default {
  name: 'CheckoutPaymentCreditCard',

  props: {
    planAmount: {
      type: Number,
      required: true,
    },

    frequency: {
      type: String,
      required: true,
    },
  },

  methods: {
    formatMoney(value) {
      return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },

    generateInstallment() {
      const amount = this.planAmount;
      let maxInstallments = 1

      switch (this.frequency) {
        case 'mensal':
          maxInstallments = 1
          break
        case 'trimestral':
          maxInstallments = 3
          break
        case 'anual':
          maxInstallments = 12
          break
      }

      this.installmentNumbers = []

      for (let i = 1; i <= maxInstallments; i++) {
        const installmentValue = amount / i

        this.installmentNumbers.push({
          text: `${i}x de ${this.formatMoney(installmentValue)}`,
          value: i,
          installment_amount: i,
          installment_value: installmentValue,
          total_value: amount
        })
      }

      this.installment_amount = 1
    },

    async submit() {
      if (!this.$refs.form.validate()) return

      this.loading = true

      try {
        const document = this.credit_card.holder.cpf_cnpj.replace(/[^0-9]+/g,'')
        const cardNumber = this.credit_card.info.number.replace(/[^0-9]+/g, '')
        const expireMonth = this.expire.split('/')[0]
        const expireYear = this.expire.split('/')[1]
        const amount = this.planAmount.toFixed(2)

        const payload = {
          payer_name: this.credit_card.holder.name,
          payer_document: document,
          installment_amount: this.installment_amount,
          method: 'ASAAS_CREDIT_CARD',
          value: amount,
          credit_card: {
            info: {
              holder_name: this.credit_card.info.holder_name,
              number: cardNumber,
              expiry_month: expireMonth,
              expiry_year: expireYear,
              ccv: this.credit_card.info.ccv,
            },
            holder: {
              name: this.credit_card.holder.name,
              email: this.credit_card.holder.email,
              cpf_cnpj: document,
              postal_code: this.credit_card.holder.postal_code,
              phone: this.credit_card.holder.phone,
              address_number: this.credit_card.holder.address_number,
            },
          },
        }

        const response = await chargeSubscription(payload)
        const data = response.data
        const status = data.installments[0]?.status
        if (status === 'PAID' || status === 'CONFIRMED') this.showSuccess = true
        else this.showSuccess = false
      } catch (error) {
        console.error('Payment error:', error)
        this.$store.dispatch('showErrorMessage', 'Ocorreu um erro ao processar o pagamento.')
      } finally {
        this.loading = false
      }
    }
  },

  computed: {
    documentMask() {
      return this.credit_card.holder.cpf_cnpj.length <= 14
        ? this.mask.cpfCnpj.cpf
        : this.mask.cpfCnpj.cnpj
    },

    documentRules() {
      return this.credit_card.holder.cpf_cnpj.length <= 14
        ? [this.rules.cpf]
        : [this.rules.cnpj]
    },
  },

  data() {
    return {
      expiryMonthError: false,
      expiryYearError: false,
      installment_amount: 1,
      installmentNumbers: [],
      expire: '',
      address: '',

      credit_card: {
        info: {
          holder_name: null,
          number: null,
          expiry_month: null,
          expiry_year: null,
          ccv: null,
        },

        holder: {
          name: '',
          email: '',
          phone: '',
          cpf_cnpj: '',
          postal_code: '',
          address_number: '',
        },
      },

      rules: {
        name,
        email,
        required,
        phoneValidation,
        expiredYearValidation,
        expiredMonthValidation,
        number: (val) => (!!val && val.length >= 19) || this.labels.required,
        expiry: (val) => (!!val && val.length >= 7) || this.labels.required,
        ccv: (val) => (!!val && val.length >= 2) || this.labels.required,
        cpf: (val) => validateCPF(val) || this.labels.invalidCPF,
        cnpj: (val) => validateCNPJ(val) || this.labels.invalidCNPJ,
      },

      labels: {
        cardInfo: this.$t('quote.label.cardInfo'),
        personalInfo: this.$t('quote.label.personalInfo'),
        required: this.$t('rules.required'),
        creditCard: this.$t('forms.creditCardNumber'),
        name: this.$t('forms.completeName'),
        email: this.$t('forms.email'),
        ccv: this.$t('forms.cardCvv'),
        cardDate: this.$t('forms.cardDate'),
        cardDateHint: this.$t('onboarding.card.dueDateHint'),
        installments: this.$t('quote.label.instalmentNumber'),
        document: this.$t('forms.cpfCnpj'),
        phone: this.$t('forms.phone'),
        postalCode: this.$t('forms.zipCode'),
        addressNumber: this.$t('forms.addressNumber'),
        address: this.$t('forms.address'),
        actionLabel: this.$t('forms.confirm'),
        invalidCPF: this.$t('onboarding.register.errors.cpf'),
        invalidCNPJ: this.$t('onboarding.register.errors.cnpj'),
      },

      mask: {
        cpfCnpj,
        phone,
      },

      loading: false,
      showSuccess: false,
    }
  },

  mounted() {
    this.generateInstallment()
  },
}
</script>
